import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";
import { NavigationLink } from "@/lib/types";
import useNavigationStore from "@/store/use-navigation";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { Mixpanel } from "@/lib/mixpanel";
import { useHasPermission } from "@/hooks/use-permission";
import { PERMISSIONS } from "@/lib/constants/permissions";

const NavigationBar = () => {
  const { links } = useNavigationStore();
  const canReadExpenses = useHasPermission(PERMISSIONS.Expenses.Read);

  return (
    <div className="fixed bottom-0 w-screen border-t border-white/10 bg-[#041935] px-4 pb-9">
      <div className="flex items-center justify-between">
        {links
          .filter((link) => !(!canReadExpenses && link.path === "/reports"))
          .map((link, index) => (
            <NavigationBarItem key={index} link={link} />
          ))}
      </div>
    </div>
  );
};

const NavigationBarItem = ({ link }: { link: NavigationLink }) => {
  const location = useLocation();
  const { t } = useTranslation();

  const isActive = useMemo(() => {
    return location.pathname === link.path;
  }, [location.pathname, link.path]);

  return (
    <Link
      data-testid={`navigation-bar-item-${link.label}`}
      to={link.path}
      className={cn(
        "relative flex flex-col items-center gap-1 pt-2 text-[#E3EDFC] transition-colors duration-200 hover:text-[#26C2DD]",
        isActive && "text-[#26C2DD]"
      )}
      onClick={() => {
        Mixpanel.track(`Navigation Bar ${link.label} Clicked`);
      }}
    >
      {isActive && (
        <span
          className="absolute -top-px h-px w-full"
          style={{
            background:
              "linear-gradient(90deg, rgba(38,194,221,0) 0%, rgba(38,194,221,1) 15%, rgba(38,194,221,1) 85%, rgba(38,194,221,0) 100%)",
          }}
        />
      )}

      {isActive && (
        <span
          style={{
            filter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
          }}
          className={cn(
            "absolute -top-1 h-3 w-full translate-y-2 rounded-b-full bg-[#38A2B9CC]"
          )}
        />
      )}
      <link.icon className="h-5 w-5" />
      <span className="text-xs font-medium">{t(link.label)}</span>
    </Link>
  );
};

export default NavigationBar;
